import { FC, PropsWithChildren } from 'react';

import { useAtomValue } from 'jotai';
import { Navigate, useLocation } from 'react-router';

import { addTag } from '@core';
import { userAtom, useSyncAtom } from '@store';

import { useUser, useUserEvents } from './hooks';
import { LoadingScreen } from './screens/LoadingScreen/LoadingScreen';

type UserProviderProps = {
    authPaths: string[];
    qrCodePath?: string;
    loginPath: string;
};

export const UserProvider: FC<PropsWithChildren<UserProviderProps>> = ({
    children,
    authPaths,
    loginPath,
    qrCodePath,
}) => {
    const { data: user, isLoading } = useUser();
    const location = useLocation();

    addTag('userId', user?.id ?? 'None');

    useUserEvents();
    useSyncAtom(userAtom, user, true);
    const userAtomValue = useAtomValue(userAtom);

    const isAuthPath = authPaths.includes(location.pathname);
    const isQrPath = qrCodePath && location.pathname.includes(qrCodePath);

    // Allow QR path immediately
    if (!isLoading && isQrPath) {
        return children;
    }

    // Redirect unauthenticated users
    if (!isLoading && !user && !isAuthPath) {
        return <Navigate replace to={loginPath} />;
    }

    /**
     * Temporary guard to prevent rendering until user state is fully synced.
     * Without this, userAtom is undefined on first render (sync happens in an effect),
     * which I do believe is the cause of the user settings bug.
     *
     * TODO: Replace with a proper initialization flow once designed.
     */
    if ((isLoading || !userAtomValue) && !isAuthPath) {
        return <LoadingScreen message="Loading user application..." />;
    }

    return children;
};
